import React, { useState } from "react";
import moment from "moment";
import { ICourseSummary } from "@eagerdog/interfaces";

import { apiService } from "src/services";
import { toast } from "src/components/Toast/ToastManager";
import { Modal } from "src/components/Modal/Modal";
import Button from "src/components/Button/Button";
import DatePick from "src/components/DatePick/DatePick";
import Input from "src/components/Input/Input";

import styles from "./AddSessionModal.module.scss";

interface IProps {
    isShown: boolean;
    course: ICourseSummary;
    onClose(): void;
    onSuccess(): void;
}

const AddSessionModal: React.FC<IProps> = (props) => {
    const defaultStart = moment().minute(0).second(0).toDate();
    const defaultEnd = moment().minute(0).second(0).add(1, "hours").toDate();

    const [start, setStart] = useState<Date>(defaultStart);
    const [end, setEnd] = useState<Date>(defaultEnd);
    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");
    const [capacity, setCapacity] = useState<number>(5);
    const [saving, setSaving] = useState<boolean>(false);

    const handleSubmit = async () => {
        if (!firstName.trim() || !lastName.trim()) {
            toast.show({ title: "Add Session", content: "Instructor first and last name are required.", duration: 5000, type: "fail" });
            return;
        }

        const newSession = {
            start_date: start,
            end_date: end,
            instructors: [{ first_name: firstName.trim(), last_name: lastName.trim(), contact_type: "Instructor", phone: "123", email: "email" }],
            regular_capacity: capacity,
            dropin_capacity: capacity,
        };

        const updatedSchedule = [
            ...(props.course.schedule || []).map((s: any) => ({
                _id: s._id,
                start_date: s.start_date,
                end_date: s.end_date,
                instructors: s.instructors,
                regular_capacity: s.regular_capacity,
                dropin_capacity: s.dropin_capacity,
            })),
            newSession,
        ];

        // Build a full CourseDto — the backend requires title, conditions, address,
        // fee, and use_early_bird_fee at minimum (all @IsNotEmpty).
        const courseDto: any = {
            title: props.course.title,
            conditions: props.course.conditions,
            address: props.course.address,
            fee: props.course.fee ?? 0,
            use_early_bird_fee: props.course.use_early_bird_fee ?? false,
            schedule: updatedSchedule,
            // carry through optional fields so nothing is wiped
            description: props.course.description,
            notes: props.course.notes,
            course_type: props.course.course_type,
            status: props.course.status,
            timezone: props.course.timezone,
            waiver: props.course.waiver,
            club_policies: props.course.club_policies,
            attachments: props.course.attachments,
            category: props.course.category,
            waitlist_limit: props.course.waitlist_limit,
            waitlist_enabled: props.course.waitlist_enabled,
            waitlist_size: props.course.waitlist_size,
            max_dogs: props.course.max_dogs,
            early_bird_fee: props.course.early_bird_fee,
            early_bird_end_date: props.course.early_bird_end_date,
            registration_end_date: props.course.registration_end_date,
            registration_message: props.course.registration_message,
            per_session_fee: (props.course as any).per_session_fee,
            use_early_bird_per_session_fee: (props.course as any).use_early_bird_per_session_fee,
            early_bird_per_session_fee: (props.course as any).early_bird_per_session_fee,
        };

        setSaving(true);
        try {
            await apiService.updateCourse(props.course._id, courseDto);
            toast.show({ title: "Add Session", content: "Session added successfully.", duration: 4000, type: "success" });
            props.onSuccess();
            props.onClose();
        } catch {
            toast.show({ title: "Add Session", content: "Failed to add session. Please try again.", duration: 5000, type: "fail" });
        } finally {
            setSaving(false);
        }
    };

    const content = (
        <div className={styles.wrap}>
            <div className={styles.title}>Add Session</div>
            <div className={styles.row}>
                <div className={styles.col}>
                    <DatePick
                        label="Start Date"
                        value={start}
                        onChange={(date: any) => {
                            setStart(date);
                            setEnd(new Date(date.getTime() + 60 * 60 * 1000));
                        }}
                        showTimeSelect={true}
                    />
                </div>
                <div className={styles.col}>
                    <DatePick
                        label="End Date"
                        value={end}
                        onChange={(date: any) => setEnd(date)}
                        showTimeSelect={true}
                        minDate={start}
                        maxDate={start}
                    />
                </div>
            </div>
            <div className={styles.row}>
                <div className={styles.col}>
                    <Input label="Instructor First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                </div>
                <div className={styles.col}>
                    <Input label="Instructor Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                </div>
            </div>
            <div className={styles.row}>
                <div className={styles.col}>
                    <Input
                        label="Capacity"
                        type="number"
                        value={capacity.toString()}
                        onChange={(e) => { const v = parseInt(e.target.value); setCapacity(isNaN(v) ? 0 : v); }}
                    />
                </div>
                <div className={styles.col} />
            </div>
            <div className={styles.actions}>
                <Button type="button" className={styles.cancelBtn} onClick={props.onClose}>Cancel</Button>
                <Button type="button" disabled={saving} onClick={handleSubmit}>
                    {saving ? "Saving…" : "Add Session"}
                </Button>
            </div>
        </div>
    );

    return (
        <Modal className="addSessionModal" isShown={props.isShown} hide={props.onClose} modalContent={content} />
    );
};

export default AddSessionModal;
